// GTM v4 DataLayer Tracking Utility
// MYOUWEB STANDARD – Form submission + click tracking with PII hashing.

declare global {
    interface Window {
        dataLayer: any[];
    }
}

const SITE_ID = 'roadymalveira';

// ============================================================
// Type vocabularies
// ============================================================

export type FormType = 'contacto' | 'orcamento' | 'recrutamento' | 'ligamos';

export type FormSubject =
    | 'revisao' | 'oleo' | 'filtros' | 'bateria' | 'pneus'
    | 'alinhamento' | 'equilibragem' | 'travoes' | 'amortecedores'
    | 'escapes' | 'ar_condicionado' | 'restauro_farol' | 'lavagem'
    | 'teste_diagnostico' | 'inspecao' | 'mecanica_geral' | 'frotas'
    | 'sem_servico_especifico'
    | FormType;

export type FormLocation =
    | 'pagina_contacto' | 'pagina_orcamento' | 'pagina_recrutamento'
    | 'pagina_ligamos' | 'pagina_servico'
    | 'popup_orcamento' | 'hero_homepage' | 'footer';

export type ClickLocation =
    | 'header' | 'footer' | 'floating_button'
    | 'contact_page' | 'service_detail' | 'hero_homepage'
    | 'budget_page' | 'pneus_page' | 'store_page' | 'help_card';

// ============================================================
// PII hashing (SHA-256 hex, lowercased + trimmed inputs)
// ============================================================

async function sha256(input: string): Promise<string> {
    const bytes = new TextEncoder().encode(input.toLowerCase().trim());
    const hashBuf = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hashBuf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

function normalizePhone(phone: string): string {
    return phone.replace(/[^\d+]/g, '');
}

// ============================================================
// Service-type → form_subject mapping (BudgetPage dropdown only)
// ============================================================

const SERVICE_TYPE_TO_SUBJECT: Record<string, FormSubject> = {
    '':                        'sem_servico_especifico',
    'revision':                'revisao',
    'workshop-services-pack':  'mecanica_geral',
    'air-conditioning':        'ar_condicionado',
    'washing':                 'lavagem',
    'headlight-restoration':   'restauro_farol',
    'battery-service':         'bateria',
    'brake-service':           'travoes',
    'oil-change':              'oleo',
    'engine-repair':           'mecanica_geral',
    'tire-services':           'pneus',
    'diagnostic-test':         'teste_diagnostico',
    'inspection':              'inspecao',
    'general-mechanics':       'mecanica_geral',
    'alignment':               'alinhamento',
    'shock-absorbers':         'amortecedores',
    'other':                   'sem_servico_especifico'
};

export function mapServiceTypeToSubject(serviceType: string | undefined | null): FormSubject {
    return SERVICE_TYPE_TO_SUBJECT[serviceType ?? ''] ?? 'sem_servico_especifico';
}

// ============================================================
// Form success tracking
// ============================================================

export async function trackFormSuccess(params: {
    form_type: FormType;
    form_subject: FormSubject;
    form_location: FormLocation;
    form_email?: string;
    form_phone?: string;
}): Promise<void> {
    try {
        const form_email_hashed = params.form_email
            ? await sha256(params.form_email)
            : undefined;
        const form_phone_hashed = params.form_phone
            ? await sha256(normalizePhone(params.form_phone))
            : undefined;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'form_success',
            site_id: SITE_ID,
            form_type: params.form_type,
            form_subject: params.form_subject,
            form_location: params.form_location,
            ...(form_email_hashed ? { form_email: form_email_hashed } : {}),
            ...(form_phone_hashed ? { form_phone: form_phone_hashed } : {})
        });
    } catch {
        // Never break form UX because of a tracking error
    }
}

// ============================================================
// Click tracking
// ============================================================

export function trackPhoneClick(click_location: ClickLocation): void {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'phone_click',
            site_id: SITE_ID,
            click_location
        });
    } catch { /* swallow */ }
}

export function trackWhatsAppClick(click_location: ClickLocation): void {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'whatsapp_click',
            site_id: SITE_ID,
            click_location
        });
    } catch { /* swallow */ }
}

export function trackEmailClick(click_location: ClickLocation): void {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'email_click',
            site_id: SITE_ID,
            click_location
        });
    } catch { /* swallow */ }
}
